import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App";
import Config from "./utils/Config";
import { camelCaseKeys } from "./utils/StringUtils";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration, captureConsoleIntegration } from "@sentry/react";
import { SubscriptionPlan } from "./pages/SubscriptionPage/SubscriptionPlan";
import axios from "axios";
import * as serviceWorker from "./serviceWorker";
import {
  setSentryShopContext,
  reportNetworkError,
  isBareConsoleNetworkError,
} from "./utils/networkErrorReporting";

const waitForRoot = () => {
  return new Promise((resolve) => {
    const el = document.getElementById("root");
    if (el) return resolve(el);
    document.addEventListener("DOMContentLoaded", () => {
      resolve(document.getElementById("root"));
    });
  });
};

const urlParams = new URLSearchParams(window.location.search);
const shopOrigin = urlParams.get("shop");
const authQueryString = urlParams.toString();
const installUrlAws = `${Config.apiGateway.URL}/authentication/install/${Config.appName}?${authQueryString}`;
const installUrlFly = `${Config.flyApi.URL}/install/${Config.appName}?${authQueryString}`;

Sentry.init({
  dsn: Config.sentryDsn,
  environment: process.env.REACT_APP_STAGE || process.env.NODE_ENV || "development",
  integrations: [
    browserTracingIntegration(),
    captureConsoleIntegration({
      levels: ['error'],
     }),
  ],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.0,

  beforeSend(event, hint) {
    // Drop AbortError — these are user navigation cancels, not real failures.
    const ex = hint && hint.originalException;
    if (ex && ex.name === "AbortError") return null;
    const exValue =
      event && event.exception && event.exception.values && event.exception.values[0];
    if (exValue && exValue.type === "AbortError") return null;
    if (exValue && /^AbortError/.test(exValue.value || "")) return null;

    // Drop the bare `[Network error]:` / `[GraphQL error]:` events that
    // captureConsoleIntegration would otherwise produce. We now ship structured
    // events via reportNetworkError instead, with full host/path/kind tags.
    if (isBareConsoleNetworkError(event)) return null;

    // Drop App Bridge "Unexpected attributes on <...>: bis_*" noise. These
    // come from browser extensions (Grammarly, scraper tools, etc.) injecting
    // `bis_size` / `bis_skin_checked` attrs into the DOM post-render; App Bridge
    // console.errors them and captureConsoleIntegration ships them to Sentry.
    const msg = (event && event.message) || (exValue && exValue.value) || "";
    if (/Unexpected attributes on <[^>]+>: bis_/.test(msg)) return null;

    return event;
  },
});

// Set the shop tag *before* any fetch so even the install/auth bootstrap
// requests get tagged. shopOrigin comes from the URL params Shopify embeds.
setSentryShopContext(shopOrigin);

const authenticate = async () => {
  try {
    // Try Fly.io first
    try {
      const { data, status } = await axios.get(installUrlFly);
      if (status === 200) {
        handleSuccessfulAuth(data);
        return;
      }
    } catch (flyError) {
      reportNetworkError(flyError, {
        url: installUrlFly,
        method: "GET",
        source: "axios",
        operationName: "authenticate.fly",
      });
      console.log("Fly.io authentication failed, falling back to AWS");
    }

    // Fallback to AWS
    const { data, status } = await axios.get(installUrlAws);
    if (status === 200) {
      handleSuccessfulAuth(data);
    } else {
      throw new Error(`Unexpected status code: ${status}`);
    }
  } catch (error) {
    reportNetworkError(error, {
      url: installUrlAws,
      method: "GET",
      source: "axios",
      operationName: "authenticate.aws",
    });
    const root = await waitForRoot();
    root.innerText = "Authentication failed, please try again";
  }
};

const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    // Ensure it executes in order (no `async` or `defer`)
    script.onload = () => {
      // all code in the script has run at this point
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

// App Bridge is loaded dynamically (not via a static <script> in <head>) because
// a static load breaks the app install flow. Keep it here.
// After the script loads we also wait for the <ui-modal> custom element to be
// registered — otherwise @shopify/app-bridge-react's <Modal> useEffect can hit
// a plain HTMLElement and throw "p.hide is not a function" on first render.
const loadAppBridge = async () => {
  await loadScript("https://cdn.shopify.com/shopifycloud/app-bridge.js");
  if (window.customElements && customElements.whenDefined) {
    await customElements.whenDefined('ui-modal');
  }
};

const fetchSessionToken = () => {
  try {
    if (window.shopify)
      window.shopify.idToken().then(x => console.log("session-token fetched"));
  } catch (error) {
    console.log("session-token authentication failed:", error);
  }
};

// Helper function to handle successful authentication
const handleSuccessfulAuth = async (data) => {
  const { OnboardingStatus, Plan, FreeDiscountsLeft, NeedsVatNumber, MissingScopes, RedirectUrl, AccessDenied, TrialUsedForPlans, HalloweenOffer } = data;
  const commonProps = {
    plan: Plan,
    freeDiscountsLeft: FreeDiscountsLeft,
    requestVatNumber: NeedsVatNumber,
    missingScopes: MissingScopes,
    onboardingStatus: camelCaseKeys(OnboardingStatus),
    accessDenied: AccessDenied,
    trialUsedForPlans: TrialUsedForPlans || {},
    halloweenOffer: HalloweenOffer || null,
  };
  
  const root = await waitForRoot();

  if (Plan >= SubscriptionPlan.LegacyStarter) {
    await loadAppBridge();
    fetchSessionToken();
    try {
      ReactDOM.render(<App {...commonProps} />, root);
    } catch (error) {
      console.error("ReactDOM.render failed:", error);
      Sentry.captureException(error, {
        tags: {
          location: "ReactDOM.render",
          plan: Plan,
          onboardingStatus: JSON.stringify(OnboardingStatus),
        },
        extra: {
          errorMessage: error.message,
          rootElement: root ? "exists" : "missing",
          commonProps: JSON.stringify(commonProps)
        }
      });
      if (root) {
        root.innerText = "Failed to load app, please refresh the page";
      }
    }
  } else {
    window.top === window.self
      ? window.location.assign(RedirectUrl)
      : ReactDOM.render(<App {...commonProps} redirectUrl={RedirectUrl} />, root);
  }
};

if (!authQueryString) {
  waitForRoot().then(root => { root.innerText = "Cannot authenticate, please reload app"; });
} else {
  authenticate();
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
